import * as React from 'react';

import { Box, Dialog, IconButton, Stack, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight, Maximize2, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { TableCopyDropdown, TableDownloadDropdown } from 'streamdown';

import {
  insertTableId,
  isTableNavVisible,
  neighborTableIndex,
  removeTableId,
} from '@modules/_shared/components/chat/chatTableViewerUtils';
import { useLocale } from '@shared/hooks/locale';
import { useTheme } from '@shared/theme/theme';

type GetTable = () => HTMLTableElement | null;

type ChatTableViewerApi = {
  register: (id: string, getTable: GetTable) => void;
  unregister: (id: string) => void;
  open: (id: string) => void;
};

// Null when this markdown tree has no viewer above it. StreamingMarkdown then
// mounts its own provider so expand still works outside the chat panel.
export const ChatTableViewerContext =
  React.createContext<ChatTableViewerApi | null>(null);

const isBeforeInDocument = (
  entries: Map<string, GetTable>,
  id: string,
  existingId: string,
): boolean => {
  const candidate = entries.get(id)?.() ?? null;
  const existing = entries.get(existingId)?.() ?? null;
  if (candidate == null || existing == null) {
    return false;
  }
  return (
    (candidate.compareDocumentPosition(existing) &
      Node.DOCUMENT_POSITION_FOLLOWING) !==
    0
  );
};

type DialogProps = {
  activeId: string | null;
  index: number;
  total: number;
  entriesRef: React.RefObject<Map<string, GetTable>>;
  containerRef?: React.RefObject<HTMLDivElement | null>;
  onClose: () => void;
  onStep: (delta: number) => void;
};

// Streamdown's own fullscreen portals a z-50 layer, which the CRM sidebar
// (1200) and chat column (1201) paint over. This dialog uses the MUI modal
// (1300) instead. With a container (the main panel beside the chat, which
// has `contain: layout paint` while the chat is open) it fills that panel and
// leaves the chat usable: no focus trap, no scroll lock. Without one it fills
// the screen right of the sidebar (0px on mobile); with no backdrop, sidebar
// clicks reach the sidebar.
const ChatTableDialog = ({
  activeId,
  index,
  total,
  entriesRef,
  containerRef,
  onClose,
  onStep,
}: DialogProps) => {
  const { t } = useLocale();
  const { colors } = useTheme();
  const [markup, setMarkup] = React.useState<string | null>(null);
  const showNav = isTableNavVisible(total);
  const label =
    showNav && index >= 0
      ? t('Table {{current}} of {{total}}', {
          current: index + 1,
          total,
        })
      : t('Expand');

  // Keep the table HTML in React state. Appending a clone into the dialog
  // with replaceChildren is wiped on the next Dialog render (focus trap /
  // transition), which left a blank fullscreen with only the close button.
  React.useLayoutEffect(() => {
    if (activeId == null) {
      setMarkup(null);
      return;
    }
    const read = () => entriesRef.current.get(activeId)?.()?.outerHTML ?? null;
    setMarkup(read());
    const source = entriesRef.current.get(activeId)?.() ?? null;
    if (source == null) {
      return;
    }
    const observer = new MutationObserver(() => {
      setMarkup(source.outerHTML);
    });
    observer.observe(source, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
    });
    return () => observer.disconnect();
  }, [activeId, entriesRef]);

  return (
    <Dialog
      open={activeId != null}
      onClose={onClose}
      fullScreen
      hideBackdrop
      container={containerRef == null ? undefined : () => containerRef.current}
      disableEnforceFocus={containerRef != null}
      disableScrollLock={containerRef != null}
      sx={
        containerRef == null
          ? { left: 'var(--app-sidebar-width, 0px)' }
          : undefined
      }
      aria-label={label}
      onKeyDown={event => {
        if (event.altKey || event.metaKey || event.ctrlKey) {
          return;
        }
        const delta =
          event.key === 'ArrowLeft' ? -1 : event.key === 'ArrowRight' ? 1 : 0;
        // A single table, or the end of the list, has nowhere to step. Leave
        // the key so the overflow container can scroll a wide table sideways.
        if (delta === 0 || neighborTableIndex(index, delta, total) == null) {
          return;
        }
        event.preventDefault();
        onStep(delta);
      }}
      slotProps={{
        paper: {
          sx: {
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: 'background.paper',
            color: 'text.primary',
          },
        },
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 1,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" sx={{ alignItems: 'center', minWidth: 0 }}>
          {showNav && index >= 0 && (
            <>
              <IconButton
                size="small"
                onClick={() => onStep(-1)}
                disabled={index <= 0}
                title={t('Previous')}
                aria-label={t('Previous')}
              >
                <ChevronLeft size={20} color={colors.black} />
              </IconButton>
              <Typography
                variant="body2"
                aria-live="polite"
                sx={{ px: 1, fontVariantNumeric: 'tabular-nums' }}
              >
                {label}
              </Typography>
              <IconButton
                size="small"
                onClick={() => onStep(1)}
                disabled={index >= total - 1}
                title={t('Next')}
                aria-label={t('Next')}
              >
                <ChevronRight size={20} color={colors.black} />
              </IconButton>
            </>
          )}
        </Stack>
        <IconButton
          size="small"
          onClick={onClose}
          title={t('Close')}
          aria-label={t('Close')}
          autoFocus
        >
          <X size={20} color={colors.black} />
        </IconButton>
      </Stack>
      <Box
        onClick={onClose}
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          backgroundColor: 'background.paper',
          color: 'text.primary',
          // Collapsed borders belong to the table, so they scroll away from
          // the sticky header and leave slits the rows show through.
          '& table': {
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: 0,
          },
          '& th, & td': {
            borderRight: 1,
            borderBottom: 1,
            borderColor: 'divider',
          },
          '& tr > :first-child': { borderLeft: 1, borderColor: 'divider' },
          '& thead tr:first-child > *': {
            borderTop: 1,
            borderColor: 'divider',
          },
          '& thead th': {
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: 'background.paper',
          },
        }}
      >
        {/* Padding lives here, not on the scroller: Chrome pins a sticky
            top: 0 at the scroller's content edge, below its padding. */}
        <Box sx={{ p: 2 }}>
          {markup != null && (
            <div
              onClick={event => event.stopPropagation()}
              dangerouslySetInnerHTML={{ __html: markup }}
            />
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export const ChatTableViewerProvider = ({
  children,
  containerRef,
}: {
  children: React.ReactNode;
  containerRef?: React.RefObject<HTMLDivElement | null>;
}) => {
  const entriesRef = React.useRef(new Map<string, GetTable>());
  const orderRef = React.useRef<readonly string[]>([]);
  const [order, setOrder] = React.useState<readonly string[]>([]);
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const { pathname } = useLocation();
  orderRef.current = order;

  // Sidebar links stay clickable under the dialog; leaving the page closes it.
  React.useEffect(() => {
    setActiveId(null);
  }, [pathname]);

  const register = React.useCallback((id: string, getTable: GetTable) => {
    entriesRef.current.set(id, getTable);
    setOrder(prev =>
      insertTableId(prev, id, (candidate, existing) =>
        isBeforeInDocument(entriesRef.current, candidate, existing),
      ),
    );
  }, []);

  const unregister = React.useCallback((id: string) => {
    entriesRef.current.delete(id);
    setOrder(prev => removeTableId(prev, id));
    setActiveId(current => (current === id ? null : current));
  }, []);

  const open = React.useCallback((id: string) => {
    setActiveId(id);
  }, []);

  const step = React.useCallback((delta: number) => {
    setActiveId(current => {
      if (current == null) {
        return current;
      }
      const ids = orderRef.current;
      const index = ids.indexOf(current);
      const next = neighborTableIndex(index, delta, ids.length);
      if (next == null) {
        return current;
      }
      return ids[next] ?? current;
    });
  }, []);

  const api = React.useMemo(
    () => ({ register, unregister, open }),
    [register, unregister, open],
  );

  const activeIndex = activeId == null ? -1 : order.indexOf(activeId);

  return (
    <ChatTableViewerContext.Provider value={api}>
      {children}
      <ChatTableDialog
        activeId={activeId}
        index={activeIndex}
        total={order.length}
        entriesRef={entriesRef}
        containerRef={containerRef}
        onClose={() => setActiveId(null)}
        onStep={step}
      />
    </ChatTableViewerContext.Provider>
  );
};

type ChatMarkdownTableProps = React.ComponentProps<'table'> & {
  node?: unknown;
};

export const ChatMarkdownTable = ({
  children,
  className,
  node: _node,
  ...rest
}: ChatMarkdownTableProps) => {
  const { t } = useLocale();
  const viewer = React.useContext(ChatTableViewerContext);
  const tableRef = React.useRef<HTMLTableElement>(null);
  const id = React.useId();

  React.useLayoutEffect(() => {
    if (viewer == null) {
      return;
    }
    viewer.register(id, () => tableRef.current);
    return () => viewer.unregister(id);
  }, [viewer, id]);

  return (
    <div data-streamdown="table-wrapper" className="my-4 flex flex-col gap-2">
      <div className="flex items-center justify-end gap-1">
        <TableCopyDropdown />
        <TableDownloadDropdown />
        {viewer != null && (
          <button
            type="button"
            className="cursor-pointer p-1 text-muted-foreground transition-all hover:text-foreground"
            title={t('Expand')}
            aria-label={t('Expand')}
            onClick={() => viewer.open(id)}
          >
            <Maximize2 size={14} />
          </button>
        )}
      </div>
      <div className="overflow-x-auto overflow-y-auto rounded-md border border-border bg-background">
        <table
          {...rest}
          ref={tableRef}
          data-streamdown="table"
          className={`w-full divide-y divide-border${
            className ? ` ${className}` : ''
          }`}
        >
          {children}
        </table>
      </div>
    </div>
  );
};
